import {filterNullish} from '@brunata-angular/util';
import {FlowSlot} from '../../shared/models/export-flow-slots.model';
import {SavedExportOutputFormat} from '../../shared/models/output-format.model';
import {ReadingResolution, readingsPerMeterPerDay} from '../../shared/models/reading-shape.model';
import {StepMenuLimit, StepMenuMessage} from './step-menu.model';
import {ExportScheduleValue} from '../../saved-exports/saved-export.model';

export const excelSheetRowLimit = 1048576;
const heavyRowsPerMeter = 1000;
const manySystems = 10;
const shorterWindowDays = 7;

export type ExportLimitSlot = Extract<FlowSlot, 'readingShape' | 'systems' | 'format' | 'schedule'>;

export type ExportLimitMessage = {
    key: string;
    params: ReadonlyArray<number | string>;
};

export type ExportLimitFinding = {
    slot: ExportLimitSlot;
    headline: ExportLimitMessage;
    details: ReadonlyArray<ExportLimitMessage>;
    adviceKeys: ReadonlyArray<string>;
};

export type ExportLimitSelection = {
    readingResolution: ReadingResolution | null;
    daysBackInTime: number | null;
    systemCount: number;
    channelCount: number;
    outputFormat: SavedExportOutputFormat;
    deliversByEmail: boolean;
};

export type ExportLimits = {
    findings: ReadonlyArray<ExportLimitFinding>;
};

export const noExportLimits: ExportLimits = {findings: []};

export function stepMenuLimits(
    limits: ExportLimits,
    formatNumber: (value: number) => string,
): ReadonlyMap<string, StepMenuLimit> {
    const menuMessage = (message: ExportLimitMessage): StepMenuMessage => ({
        key: message.key,
        params: message.params.map(param =>
            typeof param === 'number' ? formatNumber(param) : param,
        ),
    });
    return new Map(
        limits.findings.map(finding => [
            finding.slot,
            {
                headline: menuMessage(finding.headline),
                details: finding.details.map(menuMessage),
                adviceKeys: finding.adviceKeys,
            },
        ]),
    );
}

export function exportLimits(selection: ExportLimitSelection): ExportLimits {
    const {readingResolution, daysBackInTime} = selection;
    if (readingResolution === null || daysBackInTime === null) {
        return noExportLimits;
    }
    const readingsPerDay = readingsPerMeterPerDay[readingResolution];
    const rowsPerMeter = readingsPerDay * daysBackInTime;
    if (rowsPerMeter < heavyRowsPerMeter) {
        return noExportLimits;
    }
    return {
        findings: [
            readingShapeFinding(
                rowsPerMeter,
                readingsPerDay,
                daysBackInTime,
                selection.channelCount,
            ),
            systemsFinding(selection.systemCount, rowsPerMeter),
            formatFinding(selection.outputFormat, rowsPerMeter, selection.systemCount),
            deliveryFinding(selection.deliversByEmail),
        ].filter(filterNullish),
    };
}

function readingShapeFinding(
    rowsPerMeter: number,
    readingsPerDay: number,
    daysBackInTime: number,
    channelCount: number,
): ExportLimitFinding {
    const details: ExportLimitMessage[] = [
        {
            key: 'customizedexports_export_limits_shape_detail',
            params: [readingsPerDay, daysBackInTime],
        },
    ];
    const shrinkFactor = shorterWindowShrinkFactor(daysBackInTime);
    if (shrinkFactor > 1) {
        details.push({
            key: 'customizedexports_export_limits_shape_detail_shorter',
            params: [shorterWindowDays, shrinkFactor],
        });
    }
    return {
        slot: 'readingShape',
        headline: {
            key: 'customizedexports_export_limits_shape_headline',
            params: [rowsPerMeter, daysBackInTime],
        },
        details,
        adviceKeys: [
            'customizedexports_export_limits_advice_resolution',
            channelCount > 1 ? 'customizedexports_export_limits_advice_channels' : null,
        ].filter(filterNullish),
    };
}

function systemsFinding(systemCount: number, rowsPerMeter: number): ExportLimitFinding | null {
    if (systemCount < manySystems) {
        return null;
    }
    return {
        slot: 'systems',
        headline: {
            key: 'customizedexports_export_limits_systems_headline',
            params: [systemCount],
        },
        details: [
            {
                key: 'customizedexports_export_limits_systems_detail',
                params: [systemCount, rowsPerMeter * systemCount],
            },
        ],
        adviceKeys: ['customizedexports_export_limits_advice_systems'],
    };
}

function formatFinding(
    outputFormat: SavedExportOutputFormat,
    rowsPerMeter: number,
    systemCount: number,
): ExportLimitFinding | null {
    if (outputFormat !== 'EXCEL') {
        return null;
    }
    const leastRows = rowsPerMeter * Math.max(systemCount, 1);
    if (leastRows > excelSheetRowLimit) {
        return {
            slot: 'format',
            headline: {key: 'customizedexports_export_limits_excel_exceeded_headline', params: []},
            details: [
                {
                    key: 'customizedexports_export_limits_excel_exceeded_detail',
                    params: [excelSheetRowLimit, leastRows],
                },
            ],
            adviceKeys: ['customizedexports_export_limits_advice_format'],
        };
    }
    return {
        slot: 'format',
        headline: {key: 'customizedexports_export_limits_excel_headline', params: []},
        details: [
            {
                key: 'customizedexports_export_limits_excel_detail',
                params: [
                    excelSheetRowLimit,
                    rowsPerMeter,
                    Math.floor(excelSheetRowLimit / rowsPerMeter),
                ],
            },
        ],
        adviceKeys: ['customizedexports_export_limits_advice_format'],
    };
}

function deliveryFinding(deliversByEmail: boolean): ExportLimitFinding | null {
    if (!deliversByEmail) {
        return null;
    }
    return {
        slot: 'schedule',
        headline: {key: 'customizedexports_export_limits_email_headline', params: []},
        details: [{key: 'customizedexports_export_limits_email_detail', params: []}],
        adviceKeys: [],
    };
}

export function sftpConnectionRemovedLimits(
    connectionRemoved: boolean,
    schedule: ExportScheduleValue | undefined,
): ExportLimits {
    if (
        !connectionRemoved ||
        schedule?.deliveryChannel !== 'SFTP' ||
        schedule.sftpConnectionId !== null
    ) {
        return noExportLimits;
    }
    return {
        findings: [
            {
                slot: 'schedule',
                headline: {
                    key: 'savedexport_deliverylog_reason_short_connection_missing',
                    params: [],
                },
                details: [{key: 'savedexport_deliverylog_reason_connection_missing', params: []}],
                adviceKeys: [],
            },
        ],
    };
}

function shorterWindowShrinkFactor(daysBackInTime: number): number {
    return Math.round((daysBackInTime / shorterWindowDays) * 10) / 10;
}
